import { ArrayUtil, SortUtil } from "@mcleod/core";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Overlay } from "../../page/Overlay";
import { AddedSnackBarInfo } from "../../page/Snackbar";
import { Panel } from "./Panel";
import { Container } from "../../base/Container";
import { Layout } from "../layout/Layout";

export type ScreenStackChangeListener = () => void;
export type CurrentLayoutsListener = (currentContainer: Container, layouts: Layout[]) => void;

export class ScreenStack {
  private static listeners: ScreenStackChangeListener[] = [];
  private static currentLayoutListeners: CurrentLayoutsListener[] = [];
  private static screenStack: Panel[] = [];
  private static overlayStack: Overlay[] = [];
  private static tooltipStack: Panel[] = [];

  public static push(panel: Panel): void {
    ScreenStack.screenStack.push(panel);
    ScreenStack.fireListeners();
    ScreenStack.fireCurrentLayoutListeners();
  }

  public static pop(panel: Panel) {
    ArrayUtil.removeFromArray(ScreenStack.screenStack, panel);
    ScreenStack.fireListeners();
    ScreenStack.fireCurrentLayoutListeners();
  }

  public static getCurrentScreen(): Panel {
    return ScreenStack.screenStack.length > 0 ? ScreenStack.screenStack[ScreenStack.screenStack.length-1] : null;
  }

  public static pushSnackbar(snack: Panel, targetPanel?: Panel): AddedSnackBarInfo {
    const standardTarget = ScreenStack.getSnackbarTarget();
    const targetToUse = targetPanel == null ? standardTarget : targetPanel;
    //if the new snackbar is going to be added to a panel that's not the 'current' panel, it will have a lower zindex
    //than if it was added to the 'current' panel.  this means that everything with a higher zindex needs to be incremented
    //before we display the snackbar.  do this before we add the new snack to the panel so that it's not incremented too.
    if (targetPanel != null && targetPanel != standardTarget)
      ScreenStack.incrementZIndexes(targetToUse);
    const result = targetToUse.pushSnackbar(snack);
    ScreenStack.moveTopNonModalDialogsToFront();
    ScreenStack.fireListeners();
    return result;
  }

  public static popSnackbar(snack: Panel) {
    //find the snackbar's parent...may no longer be the same as the result of getSnackbarTarget() as new panels may have opened
    const snackbarParent = ScreenStack.findSnackbarParent(snack);
    if (snackbarParent == null)
      return;
    snackbarParent.popSnackbar(snack);
    ScreenStack.fireListeners();
  }

  private static findSnackbarParent(snack: Panel): Panel {
    for (let x=ScreenStack.overlayStack.length-1 ; x>=0 ; x--) {
      const overlay = ScreenStack.overlayStack[x];
      if (overlay.component instanceof Panel) {
        if (overlay.component.containsSnackbar(snack) === true)
          return overlay.component;
      }
    }
    for (let x=ScreenStack.screenStack.length-1 ; x>=0 ; x--) {
      const panel = ScreenStack.screenStack[x];
      if (panel.containsSnackbar(snack) === true)
        return panel;
    }
    return null;
  }

  public static getSnackbarTarget(): Panel {
    const currentOverlay = ScreenStack.getNewestOverlay();
    if (currentOverlay != null) {
      if (currentOverlay.component instanceof Panel) {
        //using componentType to avoid a circular reference
        const componentType = ComponentTypes.getComponentTypeName(currentOverlay.component);
        if (componentType !== "dialog")
          return currentOverlay.component;
      }
    }
    return ScreenStack.getCurrentScreen();
  }

  public static getOldestSnackbar(id?: string): Panel {
    return ScreenStack.getSnackbarTarget()?.getOldestSnackbar(id);
  }

  public static pushToast(toast: Panel, targetPanel?: Panel) {
    const standardTarget = ScreenStack.getToastTarget();
    const targetToUse = targetPanel == null ? standardTarget : targetPanel;
    //if the new toast is going to be added to a panel that's not the 'current' panel, it will have a lower zindex
    //than if it was added to the 'current' panel.  this means that everything with a higher zindex needs to be incremented
    //before we display the snackbar.  do this before we add the new snack to the panel so that it's not incremented too.
    if (targetPanel != null && targetPanel != standardTarget)
      ScreenStack.incrementZIndexes(targetToUse);
    targetToUse.pushToast(toast);
    ScreenStack.moveTopNonModalDialogsToFront();
    ScreenStack.fireListeners();
  }

  public static popToast(toast: Panel) {
    //find the toast's parent...may no longer be the same as the result of getToastTarget() as new panels may have opened
    const toastParent = ScreenStack.findToastParent(toast);
    if (toastParent == null)
      return;
    toastParent.popToast(toast);
    ScreenStack.fireListeners();
  }

  private static findToastParent(toast: Panel): Panel {
    for (let x=ScreenStack.overlayStack.length-1 ; x>=0 ; x--) {
      const overlay = ScreenStack.overlayStack[x];
      if (overlay.component instanceof Panel) {
        if (overlay.component.containsToast(toast) === true)
          return overlay.component;
      }
    }
    for (let x=ScreenStack.screenStack.length-1 ; x>=0 ; x--) {
      const panel = ScreenStack.screenStack[x];
      if (panel.containsToast(toast) === true)
        return panel;
    }
    return null;
  }

  public static getToastTarget(): Panel {
    const currentOverlay = ScreenStack.getNewestOverlay();
    if (currentOverlay != null) {
      if (currentOverlay.component instanceof Panel) {
        //using componentType to avoid a circular reference
        const componentType = ComponentTypes.getComponentTypeName(currentOverlay.component);
        if (componentType !== "dialog")
          return currentOverlay.component;
      }
    }
    return ScreenStack.getCurrentScreen();
  }

  public static getNewestToast(id?: string): Panel {
    return ScreenStack.getToastTarget()?.getNewestToast(id);
  }

  public static pushOverlay(overlay: Overlay) {
    ScreenStack.overlayStack.push(overlay);
    ScreenStack.fireListeners();
    ScreenStack.fireCurrentLayoutListeners();
  }

  public static popOverlay(overlay: Overlay) {
    ArrayUtil.removeFromArray(ScreenStack.overlayStack, overlay);
    ScreenStack.fireListeners();
    ScreenStack.fireCurrentLayoutListeners();
  }

  public static getNewestOverlay(): Overlay {
    return ArrayUtil.getLastElement(ScreenStack.overlayStack);
  }

  public static overlayInStack(overlay: Overlay): boolean {
    return ScreenStack.overlayStack.includes(overlay);
  }

  public static getAllOverlays(): Overlay[] {
    return ScreenStack.overlayStack;
  }

  public static getNewestNonModalDialog(): Panel {
    for (let x=ScreenStack.screenStack.length - 1 ; x>=0 ; x--) {
      const panel = ScreenStack.screenStack[x];
      if (panel["modal"] === false)
        return panel;
    }
    return null;
  }

  public static pushTooltip(tooltip: Panel) {
    ScreenStack.tooltipStack.push(tooltip);
    ScreenStack.fireListeners();
  }

  public static popTooltip(tooltip: Panel) {
    ArrayUtil.removeFromArray(ScreenStack.tooltipStack, tooltip);
    ScreenStack.fireListeners();
  }

  public static getNewestTooltip(): Panel {
    return ArrayUtil.getLastElement(ScreenStack.tooltipStack);
  }

  public static getOldestTooltip(): Panel {
    return ArrayUtil.getFirstElement(ScreenStack.tooltipStack);
  }

  public static getAllTooltips(): Panel[] {
    return ScreenStack.tooltipStack;
  }

  /**
   * This method will return the root panel in the screen stack, which should be the Router panel
   * @returns the Panel that is at the root of the screen stack
   */
  public static getRootScreen(): Panel {
    if (ScreenStack.screenStack.length > 0)
      return ScreenStack.screenStack[0];
    return null;
  }

  public static getHighestZIndex(): number {
    let result: number;
    for (const screen of ScreenStack.screenStack) {
      const screenZIndex = screen.getHighestZIndex();
      if (screenZIndex != null && (result == null || result < screenZIndex))
        result = screenZIndex;
    }
    for (const tooltip of ScreenStack.tooltipStack) {
      if (tooltip.zIndex != null && (result == null || result < tooltip.zIndex))
        result = tooltip.zIndex;
    }
    for (const overlay of ScreenStack.overlayStack) {
      const zIndex = overlay.zIndex;
      if (zIndex != null && (result == null || result < zIndex))
        result = zIndex;
    }
    return result;
  }

  public static getNewHighestZIndex(): number {
    let result = ScreenStack.getHighestZIndex();
    if (result == null)
      result = 45; //picked a random number so that we can tell it's the default value when inspecting elements
    else
      result += 1;
    return result;
  }

  static getZIndexComponents(sortDesc?: boolean): any[] {
    const result = [];
    for (const screen of ScreenStack.screenStack) {
      result.push(...screen.getZIndexComponents());
    }
    for (const tooltip of ScreenStack.tooltipStack) {
      if (tooltip.zIndex != null)
        result.push(tooltip);
    }
    for (const overlay of ScreenStack.overlayStack) {
      const zIndex = overlay.zIndex;
      if (zIndex != null)
        result.push(overlay);
    }
    if (sortDesc != null)
      ScreenStack._sortByZIndex(result, sortDesc);
    return result;
  }

  private static _sortByZIndex(panels: Panel[], sortDesc: boolean = true): Panel[] {
    if (panels != null) {
      panels.sort((a, b) => {
        const sortMethod = sortDesc === true ? "desc" : "asc";
        return SortUtil.compareTwoValues(a.zIndex, b.zIndex, sortMethod);
      });
    }
    return panels;
  }

  /**
   * Starting with the provided panel, increment any panel's zIndex value.  This is done so we can insert a new panel somewhere in the middle of the zIndex stack.
   * @param fromPanel
   */
  private static incrementZIndexes(fromPanel: Panel) {
    const targetsHighestZIndex = fromPanel.getHighestZIndex();
      if (targetsHighestZIndex != null) {
        const zIndexComponentsDesc = ScreenStack.getZIndexComponents(true);
        if (ArrayUtil.isEmptyArray(zIndexComponentsDesc) !== true) {
          for (const compToUpdate of zIndexComponentsDesc) {
            if (compToUpdate.zIndex > targetsHighestZIndex)
              compToUpdate.zIndex = compToUpdate.zIndex + 1;
            else
              break;
          }
        }
      }
  }

  /**
   * This method is intended to update the z-index value of non-modal Dialog boxes that are the most
   * recent things on the screen stack.  The intent is to make sure that those Dialogs would stay in front
   * of toasts/snacks, in case they are moved around the screen.
   *
   * We don't have to deal with modal Dialogs because toasts/snacks are covered up by the Overlay.
   *
   * Once we get to something other than a non-modal Dialog in the stack, quit.
   */
  public static moveTopNonModalDialogsToFront() {
    const dialogsToUpdate: Panel[] = [];
    for (let x=ScreenStack.screenStack.length-1 ; x>=0 ; x--) {
      const panel = ScreenStack.screenStack[x];
      if (panel["modal"] === false)
        dialogsToUpdate.splice(0, 0, panel);
      else
        break;
    }
    for (const panel of dialogsToUpdate) {
      panel.zIndex = this.getNewHighestZIndex();
    }
  }

  private static fireListeners() {
    for (const listener of ScreenStack.listeners)
      listener();
  }

  public static addListener(listener: ScreenStackChangeListener) {
    ScreenStack.listeners.push(listener);
  }

  public static removeListener(listener: ScreenStackChangeListener) {
    ArrayUtil.removeFromArray(ScreenStack.listeners, listener);
  }


  public static fireCurrentLayoutListeners() {
    const currentContainer = ScreenStack.getCurrentLayoutContainer();
    const currentLayout = ScreenStack.getCurrentLayouts(currentContainer);
    for (const listener of ScreenStack.currentLayoutListeners)
      listener(currentContainer, currentLayout);
  }

  public static addCurrentLayoutsListener(listener: CurrentLayoutsListener) {
    ScreenStack.currentLayoutListeners.push(listener);
  }

  public static removeCurrentLayoutsListener(listener: CurrentLayoutsListener) {
    ArrayUtil.removeFromArray(ScreenStack.currentLayoutListeners, listener);
  }

  public static getCurrentLayout(): Layout {
    return ScreenStack.getCurrentLayouts(ScreenStack.getCurrentLayoutContainer())?.[0];
  }

  private static getCurrentLayoutContainer(): Container {
    const overlay = ScreenStack.getNewestOverlay();
    const screen = ScreenStack.getCurrentScreen();
    if ((overlay?.zIndex ?? 0) > (screen?.zIndex ?? 0)) {
      return overlay.getOverlayContent();
    }
    return screen;
  }

  private static getCurrentLayouts(currentContainer: Container): Layout[] {
    const layouts = [];
    currentContainer?.forEveryChildComponent(comp => {
      if (comp instanceof Layout)
        layouts.push(comp);
    })
    return layouts;
  }
}
